/**
 * PIEPulseHistoryPage — full chronological record of every weekly
 * pulse the persona has logged, grouped by ISO week. Each row exposes
 * the action, three scores, friction tags, and reflection text.
 *
 * Lets users:
 *   • Audit their own consistency (paired with the streak pill).
 *   • Look back at what they wrote when reconciling a commitment.
 *   • Spot drift patterns across multiple commitments.
 *
 * Loaded from /api/personal/pulse/history (already returns trailing
 * 12 weeks). Empty state nudges them toward making a commitment if
 * there's nothing to display yet.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircleHeart, AlertTriangle, ArrowLeft, Flame } from 'lucide-react';
import PIELayout from './PIELayout';
import PIEUnseededEmptyState, { isUnseededError } from './PIEUnseededEmptyState';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';

const SCALE_LABELS = {
  applied: { 1: 'not at all', 2: 'rarely', 3: 'some of the time', 4: 'often', 5: 'consistently' },
  effective: { 1: 'no change', 2: 'small bump', 3: 'some lift', 4: 'clear gain', 5: 'big shift' },
  confidence: { 1: 'shaky', 2: 'wobbly', 3: 'steady', 4: 'solid', 5: 'rock-solid' },
};

export default function PIEPulseHistoryPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi.pulseHistory(12)
      .then(setData)
      .catch((e) => {
        logger.error('pulseHistory failed', e);
        setErr(e?.response?.data?.detail || 'Could not load pulse history');
      });
  }, []);

  if (err) {
    return (
      <PIELayout>
        {isUnseededError(err) ? <PIEUnseededEmptyState /> : (
          <div className="pie-card" style={{ color: 'var(--pie-danger)' }}>{err}</div>
        )}
      </PIELayout>
    );
  }
  if (!data) return <PIELayout><div className="pie-card pie-skeleton" style={{ height: 200 }} /></PIELayout>;

  const responses = data.responses || [];
  const streak = data.streak;

  return (
    <PIELayout>
      <div className="pie-fade-in" data-testid="pie-pulse-history">
        <section className="pie-hero-center">
          <Link
            to="/mirror/dashboard"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10,
              fontSize: 12, color: 'var(--pie-text-secondary)', textDecoration: 'none',
            }}
            data-testid="pie-pulse-history-back"
          >
            <ArrowLeft size={12} aria-hidden /> Back to dashboard
          </Link>
          <span className="pie-eyebrow">
            <Heart size={12} /> Your weekly pulse
          </span>
          <h1 className="pie-hero-h1">
            Your pulse, <span className="pie-accent-text">round by round</span>.
          </h1>
          <p className="pie-hero-sub">
            Every commitment you&apos;ve checked in on, in your own words. The freshest
            signal sits up top.
          </p>
          {streak && (
            <div
              data-testid="pie-pulse-history-streak"
              style={{
                marginTop: 12, display: 'inline-flex', gap: 8, alignItems: 'center',
                padding: '6px 14px', borderRadius: 999,
                background: streak.logged_weeks === streak.total_weeks
                  ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                color: streak.logged_weeks === streak.total_weeks ? '#065F46' : '#92400E',
                fontSize: 12, fontWeight: 600,
              }}
            >
              <Flame size={13} aria-hidden />
              {streak.logged_weeks} of last {streak.total_weeks} weeks logged
            </div>
          )}
        </section>

        {responses.length === 0 ? (
          <PulseHistoryEmpty />
        ) : (
          <section style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {responses.map((p) => <PulseRecordCard key={p.id} pulse={p} />)}
          </section>
        )}
      </div>
    </PIELayout>
  );
}

function PulseHistoryEmpty() {
  return (
    <div
      className="pie-card"
      style={{ marginTop: 28, padding: '32px 28px', textAlign: 'center' }}
      data-testid="pie-pulse-history-empty"
    >
      <Heart size={28} style={{ color: 'var(--pie-accent)', margin: '0 auto 12px' }} aria-hidden />
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 6 }}>
        No pulses logged yet
      </h2>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', maxWidth: 420, margin: '0 auto 16px' }}>
        Pulses unlock when you&apos;ve made a commitment. Pick something to focus on this
        week and the pulse prompt will show up on your dashboard.
      </p>
      <Link to="/mirror/improve" className="pie-btn pie-btn-primary" data-testid="pie-pulse-history-make-commitment">
        Pick something to improve
      </Link>
    </div>
  );
}

function PulseRecordCard({ pulse }) {
  const submitted = pulse.submitted_at
    ? new Date(pulse.submitted_at).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : pulse.week_iso;
  const tags = pulse.friction_tags || [];

  return (
    <div
      className="pie-card"
      style={{ padding: '18px 20px' }}
      data-testid={`pie-pulse-record-${pulse.id}`}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <span
          style={{
            fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase',
            color: 'var(--pie-accent)', fontWeight: 700,
          }}
        >
          {pulse.week_iso}
        </span>
        <span style={{ fontSize: 11, color: 'var(--pie-text-muted)' }}>{submitted}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 10 }}>
        {pulse.action_label || pulse.action_id}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <ScoreChip label="Applied" value={pulse.behavior_application} hint={SCALE_LABELS.applied[pulse.behavior_application]} />
        <ScoreChip label="Effective" value={pulse.perceived_effectiveness} hint={SCALE_LABELS.effective[pulse.perceived_effectiveness]} />
        <ScoreChip label="Confident" value={pulse.confidence} hint={SCALE_LABELS.confidence[pulse.confidence]} />
      </div>

      {pulse.friction && tags.length > 0 && (
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(245, 158, 11, 0.10)',
            border: '1px solid rgba(245, 158, 11, 0.30)',
            color: '#92400E', fontSize: 11, fontWeight: 600,
            marginBottom: 10,
          }}
          data-testid={`pie-pulse-record-friction-${pulse.id}`}
        >
          <AlertTriangle size={11} aria-hidden />
          friction: {tags.join(', ')}
        </div>
      )}

      {pulse.reflection && (
        <div
          style={{
            marginTop: 6, padding: '10px 14px', borderRadius: 8,
            background: 'var(--pie-surface)',
            fontSize: 13, color: 'var(--pie-text)', fontStyle: 'italic',
            lineHeight: 1.55,
          }}
        >
          &ldquo;{pulse.reflection}&rdquo;
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Link
          to="/mirror/coach"
          className="pie-btn pie-btn-ghost"
          data-testid={`pie-pulse-record-coach-${pulse.id}`}
          style={{ fontSize: 11 }}
        >
          <MessageCircleHeart size={12} /> Talk this over
        </Link>
      </div>
    </div>
  );
}

function ScoreChip({ label, value, hint }) {
  return (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontSize: 10, color: 'var(--pie-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--pie-text)', marginTop: 2 }}>
        {value}<span style={{ fontSize: 11, color: 'var(--pie-text-muted)', fontWeight: 400 }}>/5</span>
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: 'var(--pie-text-secondary)', marginTop: 1 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
